const SELECTORS = {
	purchasePrice: "#input-pp",
	downPaymentAmount: "#input-dpa",
	downPaymentPercent: "#input-percent",
	annualRatePercent: "#input-arp",
	months: "#input-months",
	years: "#input-years",
	rangeSlider: "#range-slider-loan",
	calculateBtn: "#btn-calculate",
	resultTable: ".result-table",
	monthlyPayment: ".js-monthly-payment",
	loanAmount: ".js-loan-amount",
	downPayment: ".js-down-payment",
	totalInterest: ".js-total-interest",
	totalAmountPaid: ".js-total-amount-paid",
};

const SLIDER_CONFIG = {
	skin: "round",
	grid: false,
	min: 0,
	max: 100,
	step: 1,
	hide_min_max: true,
	hide_from_to: true,
};

const moneyFormatter = new Intl.NumberFormat("en-US", {
	maximumFractionDigits: 2,
});

function formatNumber(num) {
	const n = parseFloat(num);
	return Number.isFinite(n) ? moneyFormatter.format(n) : "0";
}

function parseNumber(val) {
	return parseFloat(String(val).replace(/,/g, "")) || 0;
}

function cleanLeadingZeros(val) {
	val = val.replace(/^0+(?!\.)/, "");
	return val === "" || val.startsWith(".") ? "0" + val : val;
}

function handleMoneyInput(e) {
	let val = e.target.value.replace(/[^\d.]/g, "");

	const parts = val.split(".");
	if (parts.length > 2) {
		val = parts[0] + "." + parts.slice(1).join("");
	}

	val = cleanLeadingZeros(val);

	const [intPart = "", decPart = ""] = val.split(".");
	const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

	if (decPart !== "") {
		e.target.value = `${formattedInt}.${decPart}`;
	} else if (val.endsWith(".")) {
		e.target.value = `${formattedInt}.`;
	} else {
		e.target.value = formattedInt;
	}
}

function handlePercentInput(e) {
	let val = e.target.value.replace(/\D/g, "");
	val = cleanLeadingZeros(val);
	if (parseInt(val, 10) > 100) val = "100";
	e.target.value = val;
}

function handleRateInput(e) {
	let val = e.target.value.replace(/[^\d.]/g, "");
	const parts = val.split(".");
	if (parts.length > 2) val = parts[0] + "." + parts.slice(1).join("");
	e.target.value = cleanLeadingZeros(val);
}

function handleIntegerInput(e) {
	e.target.value = cleanLeadingZeros(e.target.value.replace(/\D/g, ""));
}

function handleYearsInput(e) {
	let val = e.target.value.replace(/[^\d.]/g, "");
	const parts = val.split(".");
	if (parts.length > 2) val = parts[0] + "." + parts.slice(1).join("");
	if (parts[1]?.length > 1) val = parts[0] + "." + parts[1][0];
	e.target.value = cleanLeadingZeros(val);
}

export function initLoanCalculator() {
	const priceEl = document.querySelector(SELECTORS.purchasePrice);
	const sliderEl = document.querySelector(SELECTORS.rangeSlider);
	const calculateBtn = document.querySelector(SELECTORS.calculateBtn);
	const resultTable = document.querySelector(SELECTORS.resultTable);

	if (!priceEl || !sliderEl || !calculateBtn) return;
	if (typeof window.jQuery === "undefined") return;
	if (!window.jQuery.fn?.ionRangeSlider) return;

	const $ = window.jQuery;

	const els = {
		purchasePrice: priceEl,
		downPaymentAmount: document.querySelector(SELECTORS.downPaymentAmount),
		downPaymentPercent: document.querySelector(SELECTORS.downPaymentPercent),
		annualRatePercent: document.querySelector(SELECTORS.annualRatePercent),
		months: document.querySelector(SELECTORS.months),
		years: document.querySelector(SELECTORS.years),
		monthlyPayment: document.querySelectorAll(SELECTORS.monthlyPayment),
		loanAmount: document.querySelector(SELECTORS.loanAmount),
		downPayment: document.querySelector(SELECTORS.downPayment),
		totalInterest: document.querySelector(SELECTORS.totalInterest),
		totalAmountPaid: document.querySelector(SELECTORS.totalAmountPaid),
	};

	let isUpdating = false;
	let isResultVisible = false;
	let slider = null;

	$(sliderEl).ionRangeSlider({
		...SLIDER_CONFIG,
		onChange: (data) => onSliderChange(data.from),
		onUpdate: (data) => onSliderChange(data.from),
	});
	slider = $(sliderEl).data("ionRangeSlider");

	function onSliderChange(percent) {
		if (isUpdating) return;
		isUpdating = true;
		els.downPaymentPercent.value = percent;
		updateDownPaymentFromPercent(percent);
		isUpdating = false;
	}

	function getPurchasePrice() {
		return parseNumber(els.purchasePrice.value);
	}

	function getDownPaymentAmount() {
		return parseNumber(els.downPaymentAmount.value);
	}

	function getDownPaymentPercent() {
		return parseInt(els.downPaymentPercent.value, 10) || 0;
	}

	function updateDownPaymentFromPercent(percent) {
		const amount = (getPurchasePrice() * percent) / 100;
		els.downPaymentAmount.value = formatNumber(amount);
	}

	function updatePercentFromDownPayment(amount) {
		const price = getPurchasePrice();
		const percent = price > 0 ? Math.round((amount / price) * 100) : 0;
		els.downPaymentPercent.value = percent;
		if (slider && slider.old_from !== percent) {
			slider.update({ from: percent });
		}
	}

	function convertYearsToMonths(val) {
		els.months.value = Math.round((parseFloat(val) || 0) * 12);
	}

	function convertMonthsToYears(val) {
		els.years.value = ((parseInt(val, 10) || 0) / 12).toFixed(1);
	}

	function setText(els, value) {
		if (!els) return;
		if (els instanceof NodeList) {
			els.forEach((el) => (el.textContent = value));
		} else {
			els.textContent = value;
		}
	}

	function calculate() {
		const price = getPurchasePrice();
		const down = getDownPaymentAmount();
		const rate = parseFloat(els.annualRatePercent.value) || 0;
		const months = parseInt(els.months.value, 10) || 0;
		const loan = price - down;

		if (loan <= 0 || rate <= 0 || months <= 0) return;

		const r = rate / 100 / 12;
		const pow = Math.pow(1 + r, months);
		const p = (loan * r * pow) / (pow - 1);
		const total = p * months;

		setText(els.monthlyPayment, formatNumber(p));
		setText(els.loanAmount, formatNumber(loan));
		setText(els.downPayment, formatNumber(down));
		setText(els.totalInterest, formatNumber(total - loan));
		setText(els.totalAmountPaid, formatNumber(total));

		if (resultTable && !isResultVisible) {
			resultTable.classList.add("is-visible");
			isResultVisible = true;
		}
	}

	function onPriceInput(e) {
		handleMoneyInput(e);
		updateDownPaymentFromPercent(getDownPaymentPercent());
	}

	function onDownPaymentInput(e) {
		handleMoneyInput(e);
		updatePercentFromDownPayment(getDownPaymentAmount());
	}

	function onPercentInput(e) {
		handlePercentInput(e);
		const val = getDownPaymentPercent();
		updateDownPaymentFromPercent(val);
		if (slider) slider.update({ from: val });
	}

	function onMonthsInput(e) {
		handleIntegerInput(e);
		convertMonthsToYears(e.target.value);
	}

	function onYearsInput(e) {
		handleYearsInput(e);
		convertYearsToMonths(e.target.value);
	}

	els.purchasePrice.addEventListener("input", onPriceInput);
	els.downPaymentAmount?.addEventListener("input", onDownPaymentInput);
	els.downPaymentPercent?.addEventListener("input", onPercentInput);
	els.annualRatePercent?.addEventListener("input", handleRateInput);
	els.months?.addEventListener("input", onMonthsInput);
	els.years?.addEventListener("input", onYearsInput);
	calculateBtn.addEventListener("click", calculate);

	return () => {
		els.purchasePrice.removeEventListener("input", onPriceInput);
		els.downPaymentAmount?.removeEventListener("input", onDownPaymentInput);
		els.downPaymentPercent?.removeEventListener("input", onPercentInput);
		els.annualRatePercent?.removeEventListener("input", handleRateInput);
		els.months?.removeEventListener("input", onMonthsInput);
		els.years?.removeEventListener("input", onYearsInput);
		calculateBtn.removeEventListener("click", calculate);
	};
}
